import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Location { id: number; name: string; }
interface Inventory { id: number; name: string; sku: string; stock: number; location_id: number; jenis: 'bahan_baku' | 'produk_jadi'; }

interface MutationRow { source_inventory_id: number; quantity: number; }

interface Mutation {
  id: number; date: string; notes: string;
  from_location_name: string; to_location_name: string;
  username: string; item_count: number;
}

interface MutationDetail {
  mutation: {
    id: number; date: string; notes: string;
    from_location_name: string; to_location_name: string; username: string;
  };
  items: Array<{
    source_name: string; source_sku: string;
    dest_name: string; dest_stock: number; quantity: number;
  }>;
}

const today = () => new Date().toISOString().slice(0, 10);
const emptyRow = (): MutationRow => ({ source_inventory_id: 0, quantity: 1 });

export default function Mutasi() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [mutations, setMutations] = useState<Mutation[]>([]);
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState<MutationDetail | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({ date: today(), from_location_id: '', to_location_id: '', notes: '' });
  const [rows, setRows] = useState<MutationRow[]>([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const [mutRes, invRes, locRes] = await Promise.allSettled([
      api.get('/mutations/getAll'),
      api.get('/inventories/getAllInventories'),
      api.get('/locations/getAllLocations'),
    ]);
    if (mutRes.status === 'fulfilled') setMutations(mutRes.value.data);
    if (invRes.status === 'fulfilled') setInventories(invRes.value.data);
    if (locRes.status === 'fulfilled') setLocations(locRes.value.data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openAdd = () => {
    setForm({ date: today(), from_location_id: '', to_location_id: '', notes: '' });
    setRows([emptyRow()]);
    setError('');
    setShowModal(true);
  };

  const openDetail = async (id: number) => {
    try {
      const res = await api.get(`/mutations/getById/${id}`);
      setDetail(res.data);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const updateRow = (index: number, field: keyof MutationRow, value: number) => {
    setRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  // Inventories yang tersedia di lokasi asal
  const sourceInventories = form.from_location_id
    ? inventories.filter((inv) => inv.location_id === Number(form.from_location_id) && inv.stock > 0)
    : inventories.filter((inv) => inv.stock > 0);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.from_location_id) { setError('Pilih lokasi asal'); return; }
    if (!form.to_location_id) { setError('Pilih lokasi tujuan'); return; }
    if (form.from_location_id === form.to_location_id) { setError('Lokasi asal dan tujuan tidak boleh sama'); return; }
    if (rows.some((r) => !r.source_inventory_id)) { setError('Pilih item untuk setiap baris'); return; }
    if (rows.some((r) => r.quantity < 1)) { setError('Quantity harus minimal 1'); return; }

    // Cek stok cukup
    for (const row of rows) {
      const inv = inventories.find((i) => i.id === row.source_inventory_id);
      if (inv && row.quantity > inv.stock) {
        setError(`Stok "${inv.name}" tidak cukup (tersisa ${inv.stock})`);
        return;
      }
    }

    setSaving(true); setError('');
    try {
      await api.post('/mutations/create', {
        date: form.date,
        from_location_id: Number(form.from_location_id),
        to_location_id: Number(form.to_location_id),
        notes: form.notes,
        items: rows,
      });
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal menyimpan mutasi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/mutations/delete/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Gagal menghapus');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Mutasi Stok</h1>
          <p className="text-gray-500 text-sm mt-1">Transfer barang antar lokasi</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Catat Mutasi
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-3">
        <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-blue-700">
          Gunakan fitur ini untuk memindahkan stok barang antar lokasi. Contoh: produk jadi di <strong>Pusat</strong> dipindah ke <strong>ADL</strong> agar masuk katalog toko ADL. Stok otomatis diperbarui di kedua lokasi.
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tanggal</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Dari → Ke</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Operator</th>
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Catatan</th>
                  <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {mutations.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">Belum ada data mutasi</td></tr>
                ) : mutations.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 text-gray-400 text-xs">#{m.id}</td>
                    <td className="px-5 py-4 text-gray-600">{new Date(m.date).toLocaleDateString('id-ID')}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{m.from_location_name || '-'}</span>
                        <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <span className="text-sm font-medium text-indigo-700">{m.to_location_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {m.item_count} item
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{m.username}</td>
                    <td className="px-5 py-4 text-gray-500 text-xs max-w-xs truncate">{m.notes || '-'}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openDetail(m.id)}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition" title="Detail">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {isAdmin && (
                          <button onClick={() => setDeleteId(m.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Hapus">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Catat Mutasi Stok</h2>
                <p className="text-xs text-gray-400 mt-0.5">Pindahkan barang dari satu lokasi ke lokasi lain</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-5">
              {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

              {/* Tanggal + Lokasi */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tanggal *</label>
                  <input required type="date" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi Asal *</label>
                  <select required value={form.from_location_id}
                    onChange={(e) => {
                      setForm({ ...form, from_location_id: e.target.value, to_location_id: '' });
                      setRows([emptyRow()]);
                    }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Asal --</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi Tujuan *</label>
                  <select required value={form.to_location_id}
                    onChange={(e) => setForm({ ...form, to_location_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Tujuan --</option>
                    {locations
                      .filter((l) => String(l.id) !== form.from_location_id)
                      .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Catatan</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="cth: stok untuk display ADL minggu ini" />
              </div>

              {/* Daftar Item */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Barang yang Dipindah
                  </label>
                  <button type="button" onClick={() => setRows([...rows, emptyRow()])}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Tambah Baris
                  </button>
                </div>

                {/* Info jika from_location belum dipilih */}
                {!form.from_location_id && (
                  <div className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3 text-center">
                    Pilih lokasi asal dulu untuk melihat daftar barang yang tersedia
                  </div>
                )}

                {form.from_location_id && sourceInventories.length === 0 && (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-center">
                    Tidak ada barang dengan stok &gt; 0 di lokasi ini
                  </div>
                )}

                {form.from_location_id && sourceInventories.length > 0 && (
                  <div className="space-y-2">
                    {/* Header kolom */}
                    <div className="grid grid-cols-[1fr_100px_32px] gap-2 px-1">
                      <p className="text-xs text-gray-400 font-medium">Barang</p>
                      <p className="text-xs text-gray-400 font-medium text-center">Qty Pindah</p>
                      <div />
                    </div>

                    {rows.map((row, i) => {
                      const selectedInv = inventories.find((inv) => inv.id === row.source_inventory_id);
                      return (
                        <div key={i} className="grid grid-cols-[1fr_100px_32px] gap-2 items-center">
                          <div>
                            <select value={row.source_inventory_id}
                              onChange={(e) => updateRow(i, 'source_inventory_id', Number(e.target.value))}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                              <option value={0}>-- Pilih Barang --</option>
                              {sourceInventories.map((inv) => (
                                <option key={inv.id} value={inv.id}>
                                  {inv.name} (stok: {inv.stock})
                                </option>
                              ))}
                            </select>
                            {selectedInv && (
                              <p className="text-xs text-gray-400 mt-0.5 pl-1">
                                Tersedia: <span className="font-medium text-gray-600">{selectedInv.stock}</span>
                                {' · '}
                                <span className={selectedInv.jenis === 'bahan_baku' ? 'text-orange-500' : 'text-indigo-500'}>
                                  {selectedInv.jenis === 'bahan_baku' ? 'Bahan Baku' : 'Produk Jadi'}
                                </span>
                              </p>
                            )}
                          </div>
                          <input type="number" min={1}
                            max={selectedInv?.stock ?? undefined}
                            value={row.quantity}
                            onChange={(e) => updateRow(i, 'quantity', Number(e.target.value))}
                            className={`w-full border rounded-xl px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                              selectedInv && row.quantity > selectedInv.stock
                                ? 'border-red-300 bg-red-50'
                                : 'border-gray-200'
                            }`} />
                          {rows.length > 1 ? (
                            <button type="button"
                              onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                              className="flex items-center justify-center text-red-400 hover:text-red-600 w-8 h-8">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          ) : <div />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Summary Preview */}
              {form.from_location_id && form.to_location_id && rows.some((r) => r.source_inventory_id) && (
                <div className="bg-indigo-50 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-indigo-700 mb-2">Ringkasan Transfer:</p>
                  <div className="flex items-center gap-2 text-xs text-indigo-600 mb-3">
                    <span className="font-medium">{locations.find((l) => String(l.id) === form.from_location_id)?.name}</span>
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <span className="font-medium">{locations.find((l) => String(l.id) === form.to_location_id)?.name}</span>
                  </div>
                  {rows.filter((r) => r.source_inventory_id).map((r, i) => {
                    const inv = inventories.find((inv) => inv.id === r.source_inventory_id);
                    if (!inv) return null;
                    const over = r.quantity > inv.stock;
                    return (
                      <div key={i} className="flex items-center gap-3 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${over ? 'bg-red-100 text-red-600' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          {inv.name}
                        </span>
                        <span className="text-gray-400">×</span>
                        <span className={`font-semibold ${over ? 'text-red-500' : 'text-indigo-700'}`}>{r.quantity}</span>
                        {over && <span className="text-red-500 font-medium">⚠ melebihi stok!</span>}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                  Batal
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan & Transfer Stok'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">Detail Mutasi #{detail.mutation.id}</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Info header */}
              <div className="flex items-center gap-3 bg-indigo-50 rounded-xl p-4">
                <div className="flex-1 text-center">
                  <p className="text-xs text-gray-400 mb-1">Dari</p>
                  <p className="font-semibold text-gray-800">{detail.mutation.from_location_name || '-'}</p>
                </div>
                <svg className="w-6 h-6 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <div className="flex-1 text-center">
                  <p className="text-xs text-gray-400 mb-1">Ke</p>
                  <p className="font-semibold text-indigo-700">{detail.mutation.to_location_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                {[
                  { label: 'Tanggal', value: new Date(detail.mutation.date).toLocaleDateString('id-ID', { dateStyle: 'long' }) },
                  { label: 'Operator', value: detail.mutation.username },
                  { label: 'Catatan', value: detail.mutation.notes || '-' },
                ].map((row) => (
                  <div key={row.label} className={row.label === 'Catatan' ? 'col-span-3' : ''}>
                    <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                    <p className="font-medium text-gray-800">{row.value}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Item yang Dipindahkan</p>
                <div className="space-y-2">
                  {detail.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.source_name}</p>
                        {item.source_sku && <p className="text-xs text-gray-400 font-mono">{item.source_sku}</p>}
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-700">
                          ×{item.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hapus Mutasi?</h3>
            <p className="text-gray-500 text-sm mb-6">Stok akan dikembalikan ke lokasi asal dan dikurangi dari lokasi tujuan.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                Batal
              </button>
              <button onClick={handleDelete}
                className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition">
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
